import React, { useEffect, useMemo, useState } from "react";

import {
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";

import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import dayjs from "dayjs";

const today = dayjs().format("YYYY-MM-DD");

const money = (value) => Number(value || 0).toFixed(2);

export default function OutstandingStatement() {
  const { clientId } = useClientInfo();

  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateMode, setDateMode] = useState("as_on");
  const [asOnDate, setAsOnDate] = useState(today);
  const [fromDate, setFromDate] = useState(
    dayjs().startOf("year").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(today);
  const [error, setError] = useState("");

  const loadCustomers = async () => {
    try {
      const res = await axiosInstance.get("/api/customers", {
        params: { client_id: clientId },
      });
      setCustomers(res.data || []);
    } catch (err) {
      console.error("Load customers error:", err);
      setError("Unable to load customers.");
    }
  };

  useEffect(() => {
    if (clientId) loadCustomers();
  }, [clientId]);

  const loadReport = async () => {
    if (!clientId) return;

    if (dateMode === "as_on" && !asOnDate) {
      setError("Please select As on Date.");
      return;
    }

    if (dateMode === "range") {
      if (!fromDate || !toDate) {
        setError("Please select both From Date and To Date.");
        return;
      }
      if (fromDate > toDate) {
        setError("From Date cannot be later than To Date.");
        return;
      }
    }

    try {
      setLoading(true);
      setError("");

      const params = {
        customer_id: customerId,
        date_mode: dateMode,
        client_id: clientId,
      };

      if (dateMode === "as_on") {
        params.as_on_date = asOnDate;
      } else {
        params.from_date = fromDate;
        params.to_date = toDate;
      }

      const res = await axiosInstance.get(
        "/api/accountant/outstanding-statement",
        { params },
      );

      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Outstanding statement error:", err);
      setRows([]);
      setError(
        err.response?.data?.message || "Failed to load outstanding statement.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) loadReport();
  }, [clientId]);

  const grandInvoiceTotal = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.invoice_amount || 0), 0),
    [rows],
  );

  const grandReceivedTotal = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.received_amount || 0), 0),
    [rows],
  );

  const grandBalanceTotal = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.balance || 0), 0),
    [rows],
  );

  const groupedData = useMemo(() => {
    const groups = {};
    rows.forEach((row) => {
      const name = row.customername || "Unknown Customer";
      if (!groups[name]) groups[name] = [];
      groups[name].push(row);
    });
    return groups;
  }, [rows]);

  const reportDateText =
    dateMode === "as_on"
      ? `Outstanding as on ${dayjs(asOnDate).format("DD/MM/YYYY")}`
      : `Invoice date: ${dayjs(fromDate).format("DD/MM/YYYY")} - ${dayjs(toDate).format("DD/MM/YYYY")}`;

  const exportCsv = () => {
    const csvRows = [
      [
        "Customer",
        "Date",
        "Invoice No",
        "Invoice Amount",
        "Received",
        "Balance",
        "Days",
      ],
      ...rows.map((row) => [
        row.customername,
        dayjs(row.invoice_date).format("DD/MM/YYYY"),
        row.invoice_number,
        money(row.invoice_amount),
        money(row.received_amount),
        money(row.balance),
        row.days,
      ]),
      [
        "Grand Total",
        "",
        "",
        money(grandInvoiceTotal),
        money(grandReceivedTotal),
        money(grandBalanceTotal),
        "",
      ],
    ];

    const csv = csvRows
      .map((line) =>
        line
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "OutstandingStatement.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Outstanding Statement
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reportDateText}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => window.print()}
            disabled={!rows.length}
          >
            PDF
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={exportCsv}
            disabled={!rows.length}
          >
            Excel / CSV
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "auto 1fr 1fr 1fr auto" },
          gap: 1.5,
          alignItems: "center",
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={dateMode}
          onChange={(_, value) => value && setDateMode(value)}
          sx={{ height: 40 }}
        >
          <ToggleButton value="as_on">As on Date</ToggleButton>
          <ToggleButton value="range">Date Range</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          select
          label="Customer"
          value={customerId}
          onChange={(event) => setCustomerId(event.target.value)}
          size="small"
        >
          <MenuItem value="ALL">All Customers</MenuItem>
          {customers
            .filter(
              (customer) =>
                String(customer.id ?? customer.customerid) !== "ALL",
            )
            .map((customer) => {
              const id = customer.id ?? customer.customerid;
              return (
                <MenuItem key={id} value={id}>
                  {customer.name ?? customer.customername}
                </MenuItem>
              );
            })}
        </TextField>

        {dateMode === "as_on" ? (
          <TextField
            type="date"
            label="As on Date"
            value={asOnDate}
            onChange={(event) => setAsOnDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        ) : (
          <>
            <TextField
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              type="date"
              label="To Date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </>
        )}

        <Button variant="contained" onClick={loadReport} disabled={loading}>
          Search
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#eaf1f7",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Date</TableCell>
                <TableCell>Invoice No</TableCell>
                <TableCell align="right">Invoice Amount</TableCell>
                <TableCell align="right">Received</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Days</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(groupedData).map(([customer, customerRows]) => {
                const invoiceTotal = customerRows.reduce(
                  (sum, row) => sum + Number(row.invoice_amount || 0),
                  0,
                );
                const receivedTotal = customerRows.reduce(
                  (sum, row) => sum + Number(row.received_amount || 0),
                  0,
                );
                const balanceTotal = customerRows.reduce(
                  (sum, row) => sum + Number(row.balance || 0),
                  0,
                );

                return (
                  <React.Fragment key={customer}>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ backgroundColor: "#e8f0fe", fontWeight: 700 }}
                      >
                        {customer}
                      </TableCell>
                    </TableRow>

                    {customerRows.map((row) => (
                      <TableRow key={row.invoiceid} hover>
                        <TableCell>
                          {dayjs(row.invoice_date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>{row.invoice_number}</TableCell>
                        <TableCell align="right">
                          {money(row.invoice_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {money(row.received_amount)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color:
                              Number(row.balance) > 0
                                ? "error.main"
                                : "success.main",
                          }}
                        >
                          {money(row.balance)}
                        </TableCell>
                        <TableCell align="right">{row.days}</TableCell>
                      </TableRow>
                    ))}

                    <TableRow>
                      <TableCell
                        sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}
                      >
                        {customer}
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}
                      >
                        Total
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}
                      >
                        {money(invoiceTotal)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}
                      >
                        {money(receivedTotal)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: "#f8fafc", fontWeight: 700 }}
                      >
                        {money(balanceTotal)}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f8fafc" }} />
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {rows.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{ backgroundColor: "#dfefff", fontWeight: 700 }}
                  >
                    Grand Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: "#dfefff", fontWeight: 700 }}
                  >
                    {money(grandInvoiceTotal)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: "#dfefff", fontWeight: 700 }}
                  >
                    {money(grandReceivedTotal)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: "#dfefff", fontWeight: 700 }}
                  >
                    {money(grandBalanceTotal)}
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#dfefff" }} />
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    No outstanding invoices found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
